// src/pages/products/ProductImportDialog.tsx
import React, { useMemo, useState, useEffect } from 'react';
import { DataService } from 'src/services/data-service';
import { toast } from 'react-toastify';
import GenericDialog from '../../components/GenericDialog';
import GenericFileInput from '../../components/input-components/GenericFileInput';
import { FileType } from 'src/models/file';
import * as XLSX from 'xlsx';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';

// ---------------- Shared helpers (local to this file) ----------------
type MappingRow = {
  source: string; // header in the excel/csv (as-is)
  target: string; // canonical target (core field name)
};

const CORE_TARGETS = ['Barcode', 'Name', 'Symbol'] as const;
const ADDITIONAL_TARGETS = ['size', 'Brand', 'COLOR', 'season', 'Generic', 'Category', 'MainCategory', 'gender', 'type', 'subCategory', 'material', 'style', 'collection'] as const;
const ALL_TARGETS = [...CORE_TARGETS, ...ADDITIONAL_TARGETS] as const;

type CoreTarget = typeof CORE_TARGETS[number];
type AdditionalTarget = typeof ADDITIONAL_TARGETS[number];
type AllTarget = typeof ALL_TARGETS[number];

const norm = (s: string) =>
  s?.toString().trim().replace(/\s|_|-/g, '').toLowerCase() ?? '';

const smartSuggestion = (nHeader: string): AllTarget | '' => {
  // Core targets
  if (['barcode', 'barcodes', 'code', 'productcode', 'sku'].includes(nHeader)) return 'Barcode';
  if (['name', 'productname', 'title', 'itemname', 'description', 'desc'].includes(nHeader)) return 'Name';
  if (['symbol', 'shortcode', 'stylenumber', 'styleno', 'style', 'style#', 'stylecode'].includes(nHeader)) return 'Symbol';
  
  // Additional targets - smart matching
  if (['size', 'sizes', 'sz'].includes(nHeader)) return 'size';
  if (['brand', 'brands', 'manufacturer', 'make'].includes(nHeader)) return 'Brand';
  if (['color', 'colors', 'colour', 'colours', 'clr'].includes(nHeader)) return 'COLOR';
  if (['season', 'seasons', 'seasonal'].includes(nHeader)) return 'season';
  if (['generic', 'generics', 'genericcode', 'gen'].includes(nHeader)) return 'Generic';
  if (['category', 'categories', 'cat'].includes(nHeader)) return 'Category';
  if (['maincategory', 'maincat', 'primarycategory', 'primarycat', 'main_category'].includes(nHeader)) return 'MainCategory';
  if (['gender', 'genders', 'sex'].includes(nHeader)) return 'gender';
  if (['type', 'types', 'producttype', 'itemtype'].includes(nHeader)) return 'type';
  if (['subcategory', 'subcat', 'sub_category', 'secondarycategory'].includes(nHeader)) return 'subCategory';
  if (['material', 'materials', 'fabric', 'composition'].includes(nHeader)) return 'material';
  if (['style', 'styles', 'design'].includes(nHeader)) return 'style';
  if (['collection', 'collections', 'line', 'series'].includes(nHeader)) return 'collection';
  
  return '';
};

const suggestTarget = (header: string): string => smartSuggestion(norm(header)) || '';

const requiredTargets = ['Barcode', 'Name'] as const;

// Validate duplicates for required targets
async function validateDuplicatesInFile(
  file: File,
  headerMappings: { source: string; target: string }[],
  targetsToCheck: string[] = ['Barcode', 'Name']
): Promise<{ ok: boolean; errors: string[] }> {
  try {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    const ws = wb.Sheets[sheetName];
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });

    if (!rows.length) {
      return { ok: false, errors: ['The uploaded file appears to be empty.'] };
    }

    const headers = (rows[0] || []).map((h: any) => (h ?? '').toString().trim());
    const colIndexByTarget: Record<string, number> = {};
    for (const m of headerMappings) {
      const idx = headers.findIndex(h => h === m.source);
      if (idx >= 0) colIndexByTarget[m.target] = idx;
    }

    const seen: Record<string, Set<string>> = {};
    const errors: string[] = [];

    for (const t of targetsToCheck) seen[t] = new Set();

    for (let r = 1; r < rows.length; r++) {
      for (const t of targetsToCheck) {
        const ci = colIndexByTarget[t];
        if (ci == null) continue;
        const v = (rows[r][ci] ?? '').toString().trim();
        if (!v) continue;
        if (seen[t].has(v)) {
          errors.push(`Duplicate ${t} "${v}" at row ${r + 1}`);
        } else {
          seen[t].add(v);
        }
      }
    }

    return errors.length ? { ok: false, errors } : { ok: true, errors: [] };
  } catch (e) {
    console.error('Duplicate validation failed', e);
    return { ok: false, errors: ['Failed to read/validate the file content.'] };
  }
}

// ---------------- Header Mapping Dialog (core-only) ----------------
const HeaderMappingDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  excelHeaders: string[];
  initialMappings?: MappingRow[];
  onSave: (rows: MappingRow[]) => void;
}> = ({ open, onClose, excelHeaders, initialMappings = [], onSave }) => {
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialMappings.length) {
      const mapped = new Set(initialMappings.map(m => norm(m.source)));
      const extras = excelHeaders
        .filter(h => !mapped.has(norm(h)))
        .map(h => ({ source: h, target: suggestTarget(h) || '' } as MappingRow));
      setRows([...initialMappings, ...extras]);
    } else {
      const suggested = excelHeaders.map(h => ({ source: h, target: suggestTarget(h) || '' } as MappingRow));
      setRows(suggested);
    }
  }, [open, excelHeaders, initialMappings]);

  const options = ALL_TARGETS as unknown as string[];

  const displayRows = useMemo(() => {
    return showUnmappedOnly ? rows.filter(r => !r.target) : rows;
  }, [rows, showUnmappedOnly]);

  const updateTarget = (idx: number, value: string) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], target: value?.trim() ?? '' };
      return next;
    });
  };

  const validateBeforeSave = () => {
    const mappedTargets = rows.map(r => r.target);
    for (const req of requiredTargets) {
      if (!mappedTargets.includes(req)) {
        toast.error(`Please map required field: ${req}`);
        return false;
      }
    }
    // Ensure each target is mapped at most once
    for (const ct of ALL_TARGETS) {
      const cnt = rows.filter(r => r.target === ct).length;
      if (cnt > 1) {
        toast.error(`"${ct}" must be mapped to a single header.`);
        return false;
      }
    }
    // Ensure the mapped source exists (should always be true here)
    for (const m of rows) {
      if (m.target && !excelHeaders.includes(m.source)) {
        toast.error(`Mapped header "${m.source}" was not found in the file.`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;
    try {
      setBusy(true);
      const payload = rows.filter(r => r.source && r.target);
      onSave(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">Map Headers (Products)</Typography>
          <Stack direction="row" spacing={1}>
            <Chip label="Core Properties" size="small" color="primary" variant="outlined" />
            <Chip label="Additional Properties" size="small" color="secondary" variant="outlined" />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 1 }} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              size="small"
              checked={showUnmappedOnly}
              onChange={(_, v) => setShowUnmappedOnly(v)}
            />
            <Typography variant="body2">Show unmapped only</Typography>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={`Headers: ${rows.length}`}
            variant="outlined"
          />
          <Chip
            size="small"
            color={rows.some(r => r.target === 'Barcode') ? 'success' : 'default'}
            label={rows.some(r => r.target === 'Barcode') ? 'Barcode ✓' : 'Barcode'}
            variant="outlined"
          />
          <Chip
            size="small"
            color={rows.some(r => r.target === 'Name') ? 'success' : 'default'}
            label={rows.some(r => r.target === 'Name') ? 'Name ✓' : 'Name'}
            variant="outlined"
          />
          <Chip
            size="small"
            color="info"
            label={`Additional: ${rows.filter(r => ADDITIONAL_TARGETS.includes(r.target as any)).length}`}
            variant="outlined"
          />
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>File Header</Typography>
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Maps To (Core + Additional Properties)</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, idx) => {
                const realIdx = rows.findIndex(r => r.source === row.source);
                return (
                  <TableRow key={`${row.source}-${idx}`} hover>
                    <TableCell>
                      <Typography variant="body2">{row.source}</Typography>
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        freeSolo
                        value={row.target || ''}
                        onChange={(_, v) => updateTarget(realIdx, (v as string) || '')}
                        onInputChange={(_, v) => updateTarget(realIdx, v)}
                        options={options}
                        groupBy={(option) => CORE_TARGETS.includes(option as any) ? 'Core Properties' : 'Additional Properties'}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select from list or create new property" />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">{option}</Typography>
                              {CORE_TARGETS.includes(option as any) && (
                                <Chip label="Core" size="small" color="primary" sx={{ height: 16, fontSize: '0.65rem' }} />
                              )}
                              {requiredTargets.includes(option as any) && (
                                <Chip label="Required" size="small" color="error" sx={{ height: 16, fontSize: '0.65rem' }} />
                              )}
                            </Stack>
                          </Box>
                        )}
                        isOptionEqualToValue={(opt, val) => norm(opt) === norm(val as string)}
                        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
                        filterSelectedOptions
                      />
                    </TableCell>
                  </TableRow>
                );
              })}

              {displayRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No rows to display with current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={busy}>
          Save Mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------- Main Dialog ----------------
interface ProductImportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ProductImportDialog: React.FC<ProductImportDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [parsingHeaders, setParsingHeaders] = useState(false);

  // header mapping
  const [mappingOpen, setMappingOpen] = useState(false);
  const [headerMappings, setHeaderMappings] = useState<MappingRow[]>([]);

  useEffect(() => {
    if (!open) {
      // reset on close
      setSelectedFile(null);
      setErrors([]);
      setExcelHeaders([]);
      setHeaderMappings([]);
      setParsingHeaders(false);
    }
  }, [open]);

  const handleFileChange = async (value: File | string | null) => {
    if (value instanceof File) {
      setSelectedFile(value);
      setErrors([]);
      await parseHeadersFromFile(value);
      // reset mapping when file changes
      setHeaderMappings([]);
    } else {
      setSelectedFile(null);
      setExcelHeaders([]);
      setHeaderMappings([]);
    }
  };

  const parseHeadersFromFile = async (file: File) => {
    setParsingHeaders(true);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
      const firstRow = rows?.[0] || [];
      const headers = firstRow.map((h: any) => (h ?? '').toString().trim()).filter(Boolean);
      setExcelHeaders(headers);
    } catch (e) {
      console.error('Failed to parse headers', e);
      setErrors(['Could not read headers from the uploaded file.']);
      setExcelHeaders([]);
    } finally {
      setParsingHeaders(false);
    }
  };

  const canMapHeaders = !!selectedFile && excelHeaders.length > 0 && !parsingHeaders;

  const handleOpenMapping = () => {
    if (!canMapHeaders) {
      toast.info('Please upload a file with headers first.');
      return;
    }
    setMappingOpen(true);
  };

  const handleSaveMapping = (rows: MappingRow[]) => {
    setHeaderMappings(rows);
    setMappingOpen(false);
    toast.success('Header mapping saved');
  };

  const buildHeaderMappingsPayload = (): { source: string; target: string }[] => {
    // If no manual mapping saved yet, autogenerate suggestions
    if ((!headerMappings || headerMappings.length === 0) && excelHeaders.length) {
      const autos = excelHeaders
        .map(h => ({ source: h, target: suggestTarget(h) }))
        .filter(m => m.target);
      return autos as any;
    }
    return (headerMappings || []).filter(m => m.source && m.target);
  };

  const validateRequiredMappings = (payload: { source: string; target: string }[]) => {
    const mappedTargets = payload.map(m => m.target);
    const missing = (requiredTargets as readonly string[]).filter(req => !mappedTargets.includes(req));
    if (missing.length) {
      return `Missing required mappings: ${missing.join(', ')}.`;
    }
    // ensure the mapped sources really exist in the headers
    const invalid = payload.filter(m => excelHeaders.indexOf(m.source) === -1);
    if (invalid.length) {
      return `Invalid mapping(s): ${invalid.map(i => `${i.target} -> ${i.source}`).join(', ')}.`;
    }
    // ensure all targets are unique
    for (const ct of ALL_TARGETS) {
      const cnt = payload.filter(p => p.target === ct).length;
      if (cnt > 1) return `"${ct}" must be mapped to a single header.`;
    }
    return null;
  };

  const handleSubmit = async () => {
    setErrors([]);

    if (!selectedFile) {
      setErrors(['Please select a file to upload.']);
      return;
    }
    if (excelHeaders.length === 0) {
      setErrors(['The selected file has no header row.']);
      return;
    }

    // Build mapping payload (manual or auto)
    const headerMappingsPayload = buildHeaderMappingsPayload();

    // Basic mapping validation
    const mappingErr = validateRequiredMappings(headerMappingsPayload);
    if (mappingErr) {
      setErrors([mappingErr]);
      toast.error('Please fix header mappings.');
      return;
    }

    // Duplicate validation (Barcode & Name)
    const dup = await validateDuplicatesInFile(selectedFile, headerMappingsPayload, ['Barcode', 'Name']);
    if (!dup.ok) {
      setErrors(dup.errors);
      toast.error('Duplicate values found in file.');
      return;
    }

    // Submit
    const formData = new FormData();
    formData.append('file', selectedFile);
    if (headerMappingsPayload.length) {
      formData.append('HeaderMappingsJson', JSON.stringify(headerMappingsPayload));
    }

    try {
      const response = await DataService.postForm('api/Item/upload-products', formData);
      if (response.ok) {
        toast.success('Products imported successfully!');
        onSuccess();
        setSelectedFile(null);
        setExcelHeaders([]);
        setHeaderMappings([]);
      } else {
        try {
          const errorData = await response.json();
          const errorMessage = errorData.message || 'Failed to import products.';
          setErrors([errorMessage]);
          toast.error(errorMessage);
        } catch {
          setErrors(['Failed to import products. An unknown error occurred.']);
          toast.error('Failed to import products. An unknown error occurred.');
        }
      }
    } catch (error) {
      const errorMessage = 'An error occurred while importing products.';
      setErrors([errorMessage]);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setErrors([]);
    setExcelHeaders([]);
    setHeaderMappings([]);
    onClose();
  };

  return (
    <>
      <GenericDialog
        open={open}
        onClose={handleClose}
        title="Import Products"
        onSubmit={handleSubmit}
        submitLabel="Upload"
        showSubmitButton={true}
        errors={errors}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={selectedFile ? selectedFile.name : 'No file selected'}
            size="small"
            variant="outlined"
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={handleOpenMapping}
            disabled={!canMapHeaders}
          >
            Map headers
          </Button>
          {parsingHeaders && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption">Reading headers…</Typography>
            </Stack>
          )}
        </Stack>
        <GenericFileInput
          title="Upload File"
          onChange={handleFileChange}
          allowedTypes={[FileType.csv, FileType.xls, FileType.xlsx]}
          helperText="Select a CSV/XLS/XLSX file to import products."
          value={selectedFile}
          name="product-import"
        />

        {/* Show a small hint when headers detected */}
        {excelHeaders.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Detected {excelHeaders.length} header{excelHeaders.length > 1 ? 's' : ''}. Use “Map headers” to align Barcode &amp; Name before uploading.
            </Typography>
          </Box>
        )}
      </GenericDialog>

      <HeaderMappingDialog
        open={mappingOpen}
        onClose={() => setMappingOpen(false)}
        excelHeaders={excelHeaders}
        initialMappings={headerMappings}
        onSave={handleSaveMapping}
      />
    </>
  );
};

export default ProductImportDialog;
